import {
  Briefcase,
  GraduationCap,
  HeartPulse,
  Laptop,
  Lightbulb,
  Lock,
  Package,
  Rocket,
  Scale,
  ShieldCheck,
  TrendingUp,
  Users,
  type LucideIcon,
} from 'lucide-react';

/** How many `--cover-tint-*` tokens `@hrms/ui`'s tokens.css defines. */
export const COVER_TINT_COUNT = 8;

/**
 * The cover tint (1..8) for a course category — `--cover-tint-${n}`.
 *
 * Categories are org-defined strings, so there is no enum to map from: a stable FNV-1a hash of the
 * normalised name picks the bucket, the same idea as `avatarColorIndex` in `@hrms/ui`. A course
 * card, the player header and the HR builder must all land on the same tint for "Compliance", and a
 * re-render must never reshuffle it — hence a hash, never `Math.random()`. Case and surrounding
 * whitespace are ignored so "POSH " and "posh" share a colour.
 */
export function coverTintIndex(category: string): number {
  const seed = category.trim().toLowerCase();
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (Math.abs(hash) % COVER_TINT_COUNT) + 1;
}

/** `var(--cover-tint-N)` for a category — a token reference for an inline style, never a colour. */
export function coverTintVar(category: string): string {
  return `var(--cover-tint-${coverTintIndex(category)})`;
}

/**
 * Keyword → icon for the generated cover. First match wins, so the more specific words come first
 * ("information security" is security, not information). Anything unmatched gets the generic
 * graduation cap — a category the table does not know still gets a sensible picture.
 */
const CATEGORY_ICONS: readonly (readonly [RegExp, LucideIcon])[] = [
  [/\b(posh|harass|compliance|conduct|ethic|policy|policies|legal|regulat)/, Scale],
  [/\b(secur|infosec|cyber|privacy|data protection|gdpr|password)/, Lock],
  [/\b(safety|health|fire|first aid|wellbe|wellness|ergonom)/, HeartPulse],
  [/\b(onboard|induction|orientation|welcome|joiner)/, Rocket],
  [/\b(sales|market|growth|revenue|customer)/, TrendingUp],
  [/\b(product|feature|release)/, Package],
  [/\b(tech|engineer|software|it\b|tool|systems?\b|computer)/, Laptop],
  [/\b(leader|manag|people|team|soft skill|communicat|hr\b)/, Users],
  [/\b(risk|audit|quality|iso\b)/, ShieldCheck],
  [/\b(finance|account|operation|process|business)/, Briefcase],
  [/\b(innovat|creativ|design|idea)/, Lightbulb],
];

export function courseCategoryIcon(category: string): LucideIcon {
  const name = category.trim().toLowerCase();
  for (const [pattern, icon] of CATEGORY_ICONS) {
    if (pattern.test(name)) return icon;
  }
  return GraduationCap;
}
